import axios from "axios";

export const GINJA_API_BASE = "https://api-sandbox.ginja.ai/api/v1";
export const GINJA_PARENT_URL = "https://app.sandbox.ginja.ai";
export const GINJA_COOKIE_NAME = "ginja_access_token";

/**
 * Reads the Ginja SSO token from the URL hash if present
 * (parent app redirects with #token=... to hand off the token
 * when an HttpOnly cookie can't be read cross-origin).
 * Cleans the hash from the URL after reading.
 */
export const consumeTokenFromHash = () => {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash || "";
  if (!hash.startsWith("#")) return null;
  const params = new URLSearchParams(hash.slice(1));
  const token = params.get("token") || params.get("access_token");
  if (token) {
    // Strip the token from the URL so it doesn't hang around in history
    const cleanUrl = window.location.pathname + window.location.search;
    window.history.replaceState({}, document.title, cleanUrl);
  }
  return token;
};

/**
 * Reads a cookie value by name. Returns null if not found.
 * Cookie is set on .ginja.ai domain so any subdomain can read it.
 */
export const readCookie = (name) => {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=").slice(1).join("=")) : null;
};

/**
 * Fetches the user's access context (profile + departments + permissions)
 * from the parent Ginja API.
 *
 * Uses `withCredentials: true` so the browser auto-attaches the HttpOnly
 * `ginja_access_token` cookie on `.ginja.ai`. This works even when JS
 * can't read the cookie directly. Falls back to Bearer header if token
 * was somehow read client-side.
 *
 * If the API ALSO returns the raw token in the response body (e.g. at
 * data.token or headers.token), we capture it so subsequent calls to
 * service.edencaremedical.com can use it as Bearer.
 */
export const fetchAccessContext = async (token) => {
  const response = await axios.get(`${GINJA_API_BASE}/me/access`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    withCredentials: true,
  });
  const body = response.data;
  // Look for the token in every common location a backend might put it.
  const tokenFromResponse =
    response.headers?.token ||
    response.headers?.Token ||
    response.headers?.authorization?.replace(/^Bearer\s+/i, "") ||
    response.headers?.Authorization?.replace(/^Bearer\s+/i, "") ||
    body?.data?.token ||
    body?.data?.access_token ||
    body?.data?.jwt ||
    body?.token ||
    body?.access_token ||
    body?.jwt ||
    null;
  return {
    context: body?.data,
    token: tokenFromResponse,
  };
};

export const SSO_COOKIE_SENTINEL = "sso-cookie-auth";

export const persistAccessContext = (token, context) => {
  if (!context) return;
  // If we have a raw token (from hash / response / readable cookie), store it
  // so legacy Bearer-header API calls keep working. Otherwise store a sentinel
  // so PrivateRoute sees the user as authenticated — actual auth is done by
  // the HttpOnly cookie + withCredentials on each request.
  localStorage.setItem("auth-token", token || SSO_COOKIE_SENTINEL);
  const user = context?.user || {};
  const fullName =
    user.full_name || `${user.first_name || ""} ${user.last_name || ""}`.trim();
  localStorage.setItem("name", fullName);
  localStorage.setItem("userInfo", JSON.stringify(user));
  localStorage.setItem(
    "role",
    user.is_privileged ? "Chief_Underwriter" : "Underwriter",
  );
  localStorage.setItem("accessContext", JSON.stringify(context));
};

/**
 * Redirects the user back to the parent app to log in.
 */
export const redirectToParentLogin = () => {
  const returnTo = encodeURIComponent(window.location.href);
  window.location.href = `${GINJA_PARENT_URL}?returnTo=${returnTo}`;
};
